import { apiConfig } from '@/config/apiConfig';
import inviteApiClient from './inviteApiClient';
import type {
  DocumentVersionDetails,
  ExistingSubmissionQuery,
  ExistingSubmissionResponse,
  FormSchema,
  FormSubmitValues,
  UploadedDocument,
} from '@/features/user-management/onboarding/form-builder';
import {
  getDocumentVersion as documentApiGetVersion,
  uploadDocument as documentApiUpload,
  type GetDocumentVersionArgs,
  type UploadDocumentArgs,
} from '@/features/user-management/onboarding/services/documentApi';
import {
  getLocationDistricts,
  getLocationSubdistricts,
  getLocationVillages,
  getVillageByLgd as locationApiGetVillage,
  type VillageLookupResponse,
} from '@/features/user-management/onboarding/services/locationApi';
import type {
  Subdistrict,
  Village,
} from '@/services/locationService';
import type { OnboardingCase } from '@/features/user-management/onboarding';
import type {
  InviteMeResponse,
  InviteWorkflowResponse,
  InviteWorkflowState,
} from '../types';

interface SubmitEventResponse {
  id?: number;
  message?: string;
  [key: string]: unknown;
}

/**
 * Wraps the `/console/user_onboarding/invite/*` endpoints. Always uses
 * `inviteApiClient`, never the backoffice client.
 *
 * Errors: every call surfaces the server-supplied `{message, error}` shape
 * verbatim so the candidate sees actionable copy (e.g. "invite link is
 * invalid or expired; request a new link").
 */
function wrapServerError(error: any, fallback: string): never {
  const data = error?.response?.data;
  const message =
    (data && (data.message || data.error)) || error?.message || fallback;
  const wrapped = new Error(message);
  (wrapped as any).status = error?.response?.status;
  (wrapped as any).serverError = data;
  throw wrapped;
}

class InviteService {
  /** Get the candidate's own case as scoped by the invite token. */
  async getMe(): Promise<OnboardingCase> {
    try {
      const { data } = await inviteApiClient.get<InviteMeResponse>(
        '/console/user_onboarding/invite/me',
      );
      return data.data;
    } catch (error) {
      wrapServerError(error, 'Could not load your application. Please try again.');
    }
  }

  /** Get current workflow state and events the candidate is allowed to fire. */
  async getWorkflowState(caseId: number | string): Promise<InviteWorkflowState> {
    try {
      const { data } = await inviteApiClient.get<InviteWorkflowResponse>(
        `/console/user_onboarding/invite/workflow/${encodeURIComponent(String(caseId))}`,
      );
      return data.data;
    } catch (error) {
      wrapServerError(error, 'Could not load your workflow status.');
    }
  }

  /** Form metadata for one workflow transition. */
  async getEventMetadata(caseId: number | string, event: string): Promise<FormSchema> {
    try {
      const { data } = await inviteApiClient.get<{ data: FormSchema }>(
        `/console/user_onboarding/invite/workflow/${encodeURIComponent(String(caseId))}/${encodeURIComponent(event)}`,
      );
      return data.data;
    } catch (error) {
      wrapServerError(error, 'Could not load this step.');
    }
  }

  /** Submit a workflow transition; the body matches the metadata's visible fields. */
  async submitEvent(
    caseId: number | string,
    event: string,
    payload: FormSubmitValues,
  ): Promise<SubmitEventResponse> {
    try {
      const { data } = await inviteApiClient.post<SubmitEventResponse>(
        `/console/user_onboarding/invite/workflow/${encodeURIComponent(String(caseId))}/${encodeURIComponent(event)}`,
        payload,
      );
      return data;
    } catch (error) {
      wrapServerError(error, 'Could not submit your details. Please try again.');
    }
  }

  /**
   * Look up an existing submission for `form_name` via the invite-scoped
   * mount. Used by the candidate portal to prefill workflow forms from data
   * the candidate (or PA) already entered.
   */
  async getExistingSubmission(
    formName: string,
    query: ExistingSubmissionQuery,
  ): Promise<ExistingSubmissionResponse> {
    const url = `${apiConfig.endpoints.userOnboarding.inviteExistingSubmission}/${encodeURIComponent(
      formName,
    )}/existing_submission`;
    const { data } = await inviteApiClient.get<ExistingSubmissionResponse>(url, { params: query });
    return data;
  }

  /**
   * Cascading location fetch for invite-hosted onboarding forms. Mirrors the
   * `locationController.getLocation` semantics of the MIS `/form/location`
   * family but is mounted under the invite router so candidate-token requests
   * are accepted.
   *
   * - No args → list districts (returns string[]).
   * - district only → list subdistricts.
   * - district + subdistrict → list villages.
   */
  getInviteLocationDistricts(): Promise<string[]> {
    return getLocationDistricts(inviteApiClient);
  }

  getInviteLocationSubdistricts(district: string): Promise<Subdistrict[]> {
    return getLocationSubdistricts(inviteApiClient, district);
  }

  getInviteLocationVillages(district: string, subdistrict: string): Promise<Village[]> {
    return getLocationVillages(inviteApiClient, district, subdistrict);
  }

  /**
   * Candidate-portal counterpart of `onboardingService.getVillageByLgd` —
   * uses the invite token (the candidate doesn't have a backoffice JWT).
   */
  getInviteVillageByLgd(lgdCode: string | number): Promise<VillageLookupResponse | null> {
    return locationApiGetVillage(inviteApiClient, lgdCode);
  }

  /**
   * Upload one candidate document via the invite-scoped endpoint. The server
   * records `uploaded_by_source = "CANDIDATE"` for invite-token callers.
   */
  uploadDocument(args: UploadDocumentArgs): Promise<UploadedDocument> {
    return documentApiUpload(inviteApiClient, args);
  }

  /** Fetch the presigned URL + metadata for one document version. */
  getDocumentVersion(args: GetDocumentVersionArgs): Promise<DocumentVersionDetails> {
    return documentApiGetVersion(inviteApiClient, args);
  }
}

export const inviteService = new InviteService();
export default inviteService;
